import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Brain, ArrowLeft, Lock, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../../lib/api';

const TRAIT_COLORS = {
  'Openness': '#8B5CF6',
  'Conscientiousness': '#3B82F6',
  'Extraversion': '#F59E0B',
  'Agreeableness': '#10B981',
  'Emotional Stability': '#EC4899',
  'Leadership': '#6366F1',
  'Teamwork': '#14B8A6',
  'Integrity': '#8B5CF6',
  'Stress Tolerance': '#EF4444',
  'Decision Making': '#F97316'
};

function PsychometricResults() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);

  useEffect(() => {
    if (!result) {
      fetchResult();
    }
  }, [attemptId]);

  const fetchResult = async () => {
    try {
      const response = await api.get(`/psychometric/attempt/${attemptId}`);
      setResult(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Strong';
    if (score >= 40) return 'Moderate';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-purple-600 mx-auto animate-pulse" />
          <p className="mt-4 text-lg text-gray-600">Loading Results...</p>
        </div>
      </div>
    );
  }

  // Results not visible to student
  if (result && result.results_visible === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="p-8 text-center">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Results Not Available</h1>
            <p className="text-gray-600 mb-6">
              Your psychometric test has been submitted successfully.
              The results are only visible to administrators at this time.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="bg-purple-600 hover:bg-purple-700">
              Return to Dashboard
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="flex items-center gap-4">
            <Brain className="w-12 h-12" />
            <div>
              <h1 className="text-2xl font-bold">Psychometric Assessment Results</h1>
              <p className="text-purple-200">Your personality and behavioral profile</p>
            </div>
          </div>
        </Card>

        {result && result.overall_score !== undefined && (
          <>
            {/* Overall Score */}
            <Card className="p-6 mb-6" data-testid="overall-score-card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Score</h2>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-5xl font-bold ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score?.toFixed(1)}
                  </div>
                  <div className={`text-sm font-medium mt-1 px-3 py-1 rounded-full inline-block ${getScoreBg(result.overall_score)} ${getScoreColor(result.overall_score)}`}>
                    {getScoreLabel(result.overall_score)}
                  </div>
                </div>
                <div className="w-48 h-48">
                  {/* Simple circular progress */}
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke={result.overall_score >= 60 ? '#10B981' : result.overall_score >= 40 ? '#F59E0B' : '#EF4444'}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${(result.overall_score / 100) * 251.2} 251.2`}
                      transform="rotate(-90 50 50)"
                    />
                    <text x="50" y="55" textAnchor="middle" className="text-2xl font-bold" fill="#374151">
                      {result.overall_score?.toFixed(0)}%
                    </text>
                  </svg>
                </div>
              </div>
            </Card>

            {/* Trait Scores */}
            <Card className="p-6 mb-6" data-testid="trait-scores-card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Trait Scores</h2>
              <div className="space-y-4">
                {result.trait_scores && Object.entries(result.trait_scores).map(([trait, score]) => (
                  <div key={trait} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700">{trait}</span>
                      <span className={`font-bold ${getScoreColor(score)}`}>{score?.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${score}%`,
                          backgroundColor: TRAIT_COLORS[trait] || '#8B5CF6'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Interpretation */}
            {result.interpretation && (
              <>
                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Card className="p-6" data-testid="strengths-card">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <h2 className="text-lg font-semibold text-gray-900">Strengths</h2>
                    </div>
                    {result.interpretation.strengths?.length > 0 ? (
                      <ul className="space-y-2">
                        {result.interpretation.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-green-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">No notable strengths identified.</p>
                    )}
                  </Card>
                  
                  <Card className="p-6" data-testid="weaknesses-card">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingDown className="w-5 h-5 text-orange-600" />
                      <h2 className="text-lg font-semibold text-gray-900">Areas for Development</h2>
                    </div>
                    {result.interpretation.weaknesses?.length > 0 ? (
                      <ul className="space-y-2">
                        {result.interpretation.weaknesses.map((weakness, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-orange-700">
                            <div className="w-2 h-2 bg-orange-500 rounded-full" />
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">No significant areas for improvement identified.</p>
                    )}
                  </Card>
                </div>

                {/* Summary */}
                <Card className="p-6 mb-6" data-testid="summary-card">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Summary</h2>
                  <p className="text-gray-700 leading-relaxed">
                    {result.interpretation.summary}
                  </p>
                </Card>

                {/* Development Suggestions */}
                {result.interpretation.development_suggestions?.length > 0 && (
                  <Card className="p-6" data-testid="suggestions-card">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Development Suggestions</h2>
                    <ul className="space-y-3">
                      {result.interpretation.development_suggestions.map((suggestion, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-sm font-medium text-purple-700">{idx + 1}</span>
                          </div>
                          <span className="text-gray-700">{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </>
            )}
          </>
        )}

        <div className="mt-6 text-center">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="return-dashboard-btn"
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PsychometricResults;
